/**
 * This holds the stores for the DAM
 */

import { writable, get } from "svelte/store"
import { DamProjectsService } from "@services/index.js"

const damServices = new DamProjectsService()

/**
 * @typedef {Object} ProjectName
 * @property {string} name
 * @property {string?} description
 * @property {Object?} errors
 */

/**
 * @typedef {Object} ProjectCountry
 * @property {string} country
 * @property {string} department
 * @property {string} team
 * @property {Object?} errors
 */

/**
 * @typedef {Object} ProjectContact
 * @property {string?} id
 * @property {string} name
 * @property {string?} email
 * @property {string?} phone
 * @property {Object?} errors
 */

/**
 * @typedef {Object} DamObject
 * @property {'project-name' | 'project-country' | 'project-contact' | 'finished'} state
 * @property {ProjectName | null} projectName
 * @property {ProjectCountry | null} projectCountry
 * @property {ProjectContact | null} projectContact
 */

/**
 * @callback GoBackFunction
 * @returns {void}
 */

/**
 * @typedef {Object} DamStore
 * @property {import('svelte/store').Unsubscribe} subscribe
 * @property {import('svelte/store').Set} set
 * @property {import('svelte/store').Update} update
 * @property {GoBackFunction} goBack
 * @property { () => void } reset
 */


const stepDescriptions = [
  `Define the scope of your analysis project. This is the first step of the analysis process.
`,
  "Applications that should be included.",
  "Here, you can also set more restrictive whitelists to exclude applications that can not be anonymized.",
  "",
]

const checkIfIsAvailableOrNext = (index, steps) => {
  if (index == 0) return true
  const previousStep = steps[index - 1]
  return previousStep.completed
}

/**
 *
 * @param {string | number} step
 */
const checkStepIsCompleted = (position) => {
  const _ = get(installationSetupStore)

  const findStep = _.steps.find((step) => step?.step == position)
  if (!findStep) return false
  return findStep.completed
}

export const installationStoreDefault = {
  loading: true,
  loadingMessage: "Fetching project information ...",
  steps: [],
  stepTitles: ["Project Setup", "Application Scope", "Anonymization Rules", "Main Recording"],
  stepDescriptions: [...stepDescriptions],
  handlePreviousNavigation: () => {
    const currentStep = get(installationSetupStore).currentStep

    if (currentStep === 0) {
      window.history.pushState({}, null, `/app/dam/project-installation?idproject=${get(installationSetupStore).idProject}`)
      window.location.reload()
      return
    }
    installationSetupStore.update((store) => ({
      ...store,
      currentStep: currentStep - 1,
    }))
  },
  handleNextNavigation: () => {
    const _ = get(installationSetupStore)
    const currentStep = _.currentStep
    if (currentStep == 0) {
      return handleProjectSetup(get(installationSetupStore))
    }
    if (currentStep == 1) {
      return handleApplicationScope(get(installationSetupStore))
    }
  },
  currentStep: null,
  idProject: null,
  selectedPresetIds: [],
  checkIfIsAvailableOrNext,
  checkStepIsCompleted,
  recording: {
    categories: [
      {
        name: "Blacklists",
        value: "0",
      },
      {
        name: "Whitelists",
        value: "1",
      },
    ],
    selectedCategory: "1",
  },
  applicationScope: {
    categories: [
      {
        name: "Key Strokes",
        value: "keystrokes",
      },
      {
        name: "Mouse Clicks",
        value: "mouseclicks",
      },
      {
        name: "Time",
        value: "time",
      },
    ],
    usage: {
      selectedCategory: "keystrokes",
      lengthOptions: [
        {
          name: "All",
          value: "all",
        },
        {
          name: "3",
          value: 3,
        },
        {
          name: "5",
          value: 5,
        },
        {
          name: "10",
          value: 10,
        },
      ],
      selectedLength: "all",
    },
  },
  annonymizationRules: {
    tablesKey: "annonymizationRules", //used for dynamism in the table component when adding new rules
    id: null,
    applications: [],
    classes: [],
    types: [],
    application: undefined,
    class: undefined,
    type: undefined,
  },
}
export const installationSetupStore = writable({
  ...installationStoreDefault,
})

const handleProjectSetup = async (data) => {
  const findStep = data.steps.find((step) => step?.step === 0)

  const localNav = () => {
    installationSetupStore.update((store) => {
      return {
        ...store,
        currentStep: 1,
        loading: false,
        loadingMessage: installationStoreDefault.loadingMessage,
      }
    })
    window.history.pushState({}, null, `/app/dam/project-installation?idproject=${data.idProject}&currentStep=1`)
  }
  if (findStep && findStep.completed) {
    localNav()
    return
  }
  installationSetupStore.update((store) => ({
    ...store,
    loading: true,
    loadingMessage: "Creating project ...",
  }))

  const body = {
    selectedPresetIds: data.selectedPresetIds,
    idproject: data.idProject,
  }
  await damServices.startScopingRecording(body).then(async () => {
    await damServices.completeDAMProjectStep(data.idProject, 0).then(() => {
      localNav()
      return
    })
  })
}
const handleApplicationScope = async (data) => {
  const findStep = data.steps.find((step) => step?.step === 1)
  const localNav = () => {
    installationSetupStore.update((store) => {
      return {
        ...store,
        currentStep: 2,
        loading: false,
        loadingMessage: installationStoreDefault.loadingMessage,
      }
    })
    window.history.pushState({}, null, `/app/dam/project-installation?idproject=${data.idProject}&currentStep=2`)
  }
  if (findStep && findStep.completed) {
    localNav()
    return
  }
  installationSetupStore.update((store) => ({
    ...store,
    loading: true,
    loadingMessage: "Creating project ...",
  }))

  await damServices.startAnonymizationRecording(data.idProject).then(async () => {
    await damServices.completeDAMProjectStep(data.idProject, 1).then(() => {
      localNav()
      return
    })
  })
}
